import { CONSC_LABELS } from "@/resources/constants";
import { ObsMetricName, obsMetricNames, obsMetricToLabel } from "@/resources/observations_metricUtils";
import { Consciousness, NumberRange, Observation } from "@/resources/types";
import { getLastRecordedObservationValues } from "@/util/util-obs";

// Determines how the value cells in the observation table are rendered. For
// example, a FillState of START will show a dot with a dashed line below it.
export enum FillState {
  START_END = "startend",
  START = "start",
  END = "end",
  CONTINUE = "continue",
  NONE = "none",
  ORIGIN_VALUE = "originValue",
}

export type ObsValue = number | string | NumberRange | undefined;

type ObsInputBase = { label?: string; news?: 1 | 2 | 3 | 4 };
export type ObsInputString = ObsInputBase & { value: string };
export type ObsInputRange = ObsInputBase & {
  range: NumberRange;
  /** helps to decide if "news" value here means "too high" or "too low" */
  tooHigh?: boolean;
  /** if you want to override the styling and style a ranged cell as a regular boxed cell */
  drawBorder?: boolean;
  bold?: boolean;
};
export type ObsInputOriginValue = ObsInputBase & {
  showOriginValue: boolean;
  range: NumberRange;
};

export type ObsInput = ObsInputString | ObsInputRange | ObsInputOriginValue;

type O2SaturationLimits = {
  ranges: ObsInput[];
};

export const O2_SATURATION_LIMITS: Record<string, O2SaturationLimits> = {
  default: {
    ranges: [
      { label: "≥ 94", range: { min: 94, max: 100 } },
      { label: "91-93", range: { min: 91, max: 93.999 }, news: 2 },
      { label: "85-90", range: { min: 85, max: 90.999 }, news: 3 },
      { label: "≤ 84", range: { min: -0.999, max: 84.999 }, news: 4 },
    ],
  },
  hypercapnicZeroFlow: {
    ranges: [
      { label: "≥ 88", range: { min: 88, max: 100 } },
      { label: "86-87", range: { min: 86, max: 87.999 }, news: 2 },
      { label: "84-85", range: { min: 84, max: 85.999 }, news: 3 },
      { label: "≤ 83", range: { min: -0.999, max: 83.999 }, news: 4 },
    ],
  },
  hypercapnicWithFlow: {
    ranges: [
      { label: "≥ 97", range: { min: 97, max: 100 }, news: 4 },
      { label: "95-96", range: { min: 95, max: 96.999 }, news: 3 },
      { label: "93-94", range: { min: 93, max: 94.999 }, news: 2 },
      { label: "88-92", range: { min: 88, max: 92.999 } },
      { label: "86-87", range: { min: 86, max: 87.999 }, news: 2 },
      { label: "84-85", range: { min: 84, max: 85.999 }, news: 3 },
      { label: "≤ 83", range: { min: -0.999, max: 83.999 }, news: 4 },
    ],
  },
};

export type ObservationTableSection = {
  heading: string | React.ReactNode;
  subheading: string;
  /** used to group sections together. Make sure you order grouped sections together */
  group?: string;
  groupSubheading?: string;
  dataKey: ObsMetricName | string;
  inputs: ObsInput[];
  showDefaultValue?: boolean
};

const { ALERT, CONFUSED, VERBAL, PAINFUL, UNRESP } = Consciousness;

const bloodPressureInputs: ObsInput[] = [
  { label: "≥ 200", range: { min: 200, max: 300 }, news: 3, tooHigh: true },
  { label: "190s", range: { min: 190, max: 199.999 }, news: 2, tooHigh: true },
  { label: "180s", range: { min: 180, max: 189.999 }, news: 2, tooHigh: true },
  { label: "170s", range: { min: 170, max: 179.999 }, news: 2, tooHigh: true },
  { label: "160s", range: { min: 160, max: 169.999 }, news: 1, tooHigh: true },
  { label: "150s", range: { min: 150, max: 159.999 } },
  { label: "140s", range: { min: 140, max: 149.999 } },
  { label: "130s", range: { min: 130, max: 139.999 } },
  { label: "120s", range: { min: 120, max: 129.999 } },
  { label: "110s", range: { min: 110, max: 119.999 } },
  { label: "100s", range: { min: 100, max: 109.999 }, news: 2 },
  { label: "90s", range: { min: 90, max: 99.999 }, news: 3 },
  { label: "80s", range: { min: 80, max: 89.999 }, news: 4 },
  { label: "70s", range: { min: 70, max: 79.999 }, news: 4 },
  { label: "60s", range: { min: 60, max: 69.999 }, news: 4 },
  { label: "50s", range: { min: 50, max: 59.999 }, news: 4 },
  { label: "40s", range: { min: 40, max: 49.999 }, news: 4 },
];

const heartRateInputs: ObsInput[] = [
  { label: "≥ 140", range: { min: 140, max: 200 }, news: 4, tooHigh: true },
  { label: "130s", range: { min: 130, max: 139.999 }, news: 3, tooHigh: true },
  { label: "120s", range: { min: 120, max: 129.999 }, news: 3, tooHigh: true },
  { label: "110s", range: { min: 110, max: 119.999 }, news: 2, tooHigh: true },
  { label: "100s", range: { min: 100, max: 109.999 }, news: 1 },
  { label: "90s", range: { min: 90, max: 99.999 } },
  { label: "80s", range: { min: 80, max: 89.999 } },
  { label: "70s", range: { min: 70, max: 79.999 } },
  { label: "60s", range: { min: 60, max: 69.999 } },
  { label: "50s", range: { min: 50, max: 59.999 }, news: 1 },
  { label: "40s", range: { min: 40, max: 49.999 }, news: 3 },
  { label: "≤ 30", range: { min: 0, max: 30 }, news: 4 },
];

/**
 * See also: `getVitalsSignsTableSections`, which we will use to dynamically rewrite some sections
 * based on the observations (for the `ObservationTable` component). For the `ObservationChart`, we will
 * use the static sections below.
 */
export const vitalsSignsTableSections: ObservationTableSection[] = [
  {
    heading: obsMetricToLabel("respirations"),
    subheading: "(breaths / min)",
    dataKey: obsMetricNames.respirations,
    inputs: [
      { label: "≥ 36", range: { min: 36, max: 999 }, news: 4, tooHigh: true },
      { label: "30-35", range: { min: 30, max: 35.999 }, news: 3, tooHigh: true },
      { label: "25-29", range: { min: 25, max: 29.999 }, news: 2, tooHigh: true },
      { label: "21-24", range: { min: 21, max: 24.999 }, news: 1 },
      { label: "15-20", range: { min: 15, max: 20.999 } },
      { label: "10-14", range: { min: 10, max: 14.999 } },
      { label: "5-9", range: { min: 5, max: 9.999 }, news: 3 },
      { label: "≤ 4", range: { min: -999, max: 4.999 }, news: 4 },
    ],
  },
  {
    heading: <>O<sub>2</sub> Saturation</>,
    subheading: "(percentage)",
    dataKey: obsMetricNames.o2Sats,
    inputs: O2_SATURATION_LIMITS.default.ranges,
  },
  {
    heading: <>O<sub>2</sub> Flow Rate</>,
    subheading: "(L / min)",
    dataKey: obsMetricNames.o2Flow,
    inputs: [
      { label: "≥ 10", range: { min: 10, max: 20 }, news: 3, tooHigh: true },
      { label: "7-9", range: { min: 7, max: 9.999 }, news: 2, tooHigh: true },
      { label: "3-6", range: { min: 3, max: 6.999 }, tooHigh: true },
      { label: "≤ 2", range: { min: 0, max: 2.999 } },
    ],
  },
  {
    heading: obsMetricToLabel("heartRate"),
    subheading: "(beats / min)",
    dataKey: obsMetricNames.heartRate,
    inputs: heartRateInputs,
  },
  {
    heading: obsMetricToLabel("bloodPressure"),
    subheading: "(mmHg)",
    dataKey: obsMetricNames.bloodPressure,
    inputs: bloodPressureInputs,
  },
  {
    heading: obsMetricToLabel("temperature"),
    subheading: "(°C)",
    dataKey: obsMetricNames.temperature,
    inputs: [
      { label: "≥ 39", range: { min: 39, max: 55 }, news: 3, tooHigh: true },
      { label: "38.5-38.9", range: { min: 38.5, max: 38.999 }, news: 2, tooHigh: true },
      { label: "38.0-38.4", range: { min: 38, max: 38.4999 }, news: 2, tooHigh: true },
      { label: "37.5-37.9", range: { min: 37.5, max: 37.999 } },
      { label: "37.0-37.4", range: { min: 37, max: 37.4999 } },
      { label: "36.5-36.9", range: { min: 36.5, max: 36.999 } },
      { label: "36.0-36.4", range: { min: 36, max: 36.4999 } },
      { label: "35.5-35.9", range: { min: 35.5, max: 35.999 } },
      { label: "35.1-35.4", range: { min: 35.1, max: 35.4999 } },
      { label: "≤ 35", range: { min: 0, max: 35.0999 }, news: 2 },
    ],
  },
  {
    heading: obsMetricToLabel("consciousness"),
    subheading: "(ACVPU)",
    dataKey: obsMetricNames.consciousness,
    inputs: [
      { label: CONSC_LABELS[ALERT], value: ALERT },
      { label: CONSC_LABELS[CONFUSED], value: CONFUSED, news: 2 },
      { label: CONSC_LABELS[VERBAL], value: VERBAL, news: 2 },
      { label: CONSC_LABELS[PAINFUL], value: PAINFUL, news: 3 },
      { label: CONSC_LABELS[UNRESP], value: UNRESP, news: 4 },
    ],
  },
  {
    heading: obsMetricToLabel("riskScore"),
    subheading: "",
    dataKey: obsMetricNames.riskScore,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 300 } }
    ],
  },
  {
    heading: obsMetricToLabel("hypercapnicRespiratoryFailure"),
    subheading: "",
    dataKey: obsMetricNames.hypercapnicRespiratoryFailure,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 1 } }
    ],
  },
  {
    heading: obsMetricToLabel("rhythm"),
    subheading: "",
    dataKey: obsMetricNames.rhythm,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 300 } }
    ],
  },
  {
    heading: obsMetricToLabel("bloodGlucose"),
    subheading: "",
    dataKey: obsMetricNames.bloodGlucose,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("bloodAlcohol"),
    subheading: "",
    dataKey: obsMetricNames.bloodAlcohol,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 1 } }
    ],
  },
  {
    heading: obsMetricToLabel("painScore"),
    subheading: "",
    dataKey: obsMetricNames.painScore,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } }
    ],
  },
];

export const bedsideTableSections: ObservationTableSection[] = [
  {
    heading: obsMetricToLabel("bedsideBloodPressureLaying"),
    subheading: "(mmHg)",
    dataKey: obsMetricNames.bedsideBloodPressureLaying,
    inputs: [
      { showOriginValue: true, range: { min: 40, max: 300 } }
    ],
  },
  {
    heading: obsMetricToLabel("bedsideBloodPressureLeftArm"),
    subheading: "(mmHg)",
    dataKey: obsMetricNames.bedsideBloodPressureLeftArm,
    inputs: [
      { showOriginValue: true, range: { min: 40, max: 300 } }
    ],
  },
  {
    heading: obsMetricToLabel("bedsideBloodPressureRightArm"),
    subheading: "(mmHg)",
    dataKey: obsMetricNames.bedsideBloodPressureRightArm,
    inputs: [
      { showOriginValue: true, range: { min: 40, max: 300 } }
    ],
  },
  {
    heading: obsMetricToLabel("bedsideBloodPressureStanding"),
    subheading: "(mmHg)",
    dataKey: obsMetricNames.bedsideBloodPressureStanding,
    inputs: [
      { showOriginValue: true, range: { min: 40, max: 300 } }
    ],
  },
  {
    heading: obsMetricToLabel("bedsideHeartRate"),
    subheading: "(bpm)",
    dataKey: obsMetricNames.bedsideHeartRate,
    inputs: [
      { showOriginValue: true, range: { min: 40, max: 300 } }
    ],
  },
];

export const ventilatorTableSections: ObservationTableSection[] = [
  {
    heading: obsMetricToLabel("ventilatorTidalVolume"),
    dataKey: obsMetricNames.ventilatorTidalVolume,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 6000 } }
    ],
  },
  {
    heading: obsMetricToLabel("ventilatorMinuteVentilation"),
    dataKey: obsMetricNames.ventilatorMinuteVentilation,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 360 } }
    ],
  },
  {
    heading: obsMetricToLabel("ventilatorFrequencyRate"),
    dataKey: obsMetricNames.ventilatorFrequencyRate,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 80 } }
    ],
  },
  {
    heading: obsMetricToLabel("ventilatorPeakPressure"),
    dataKey: obsMetricNames.ventilatorPeakPressure,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("ventilatorEndTidalCarbonDioxide"),
    dataKey: obsMetricNames.ventilatorEndTidalCarbonDioxide,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
];
export const urinalysisTableSections: ObservationTableSection[] = [
  {
    heading: obsMetricToLabel("urinalysisPregnancy"),
    dataKey: obsMetricNames.urinalysisPregnancy,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisAbnormalities"),
    dataKey: obsMetricNames.urinalysisAbnormalities,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 6000 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisDistream"),
    dataKey: obsMetricNames.urinalysisDistream,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 6000 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisLeukocytes"),
    dataKey: obsMetricNames.urinalysisLeukocytes,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 360 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisNitrite"),
    dataKey: obsMetricNames.urinalysisNitrite,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 80 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisUrobilinogen"),
    dataKey: obsMetricNames.urinalysisUrobilinogen,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisProtein"),
    dataKey: obsMetricNames.urinalysisProtein,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisPh"),
    dataKey: obsMetricNames.urinalysisPh,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisBlood"),
    dataKey: obsMetricNames.urinalysisBlood,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisSpecificGravity"),
    dataKey: obsMetricNames.urinalysisSpecificGravity,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisKetone"),
    dataKey: obsMetricNames.urinalysisKetone,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisBilirubin"),
    dataKey: obsMetricNames.urinalysisBilirubin,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
  {
    heading: obsMetricToLabel("urinalysisGlucose"),
    dataKey: obsMetricNames.urinalysisGlucose,
    subheading: "",
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } }
    ],
  },
];

export const neurologicalTableSections: ObservationTableSection[] = [
  // glasgow coma score
  {
    heading: "Eye Opening",
    subheading: "",
    group: "Glasgow Coma Score",
    dataKey: obsMetricNames.gcsEyes,
    inputs: [
      { label: "4 Spontaneous", value: "4", range: { min: 4, max: 4 }, drawBorder: true },
      { label: "3 To sound", value: "3", range: { min: 3, max: 3 }, drawBorder: true },
      { label: "2 To pressure", value: "2", range: { min: 2, max: 2 }, drawBorder: true },
      { label: "1 None", value: "1", range: { min: 1, max: 1 }, drawBorder: true },
      { label: "Closed", value: "C", range: { min: 0, max: 0 }, drawBorder: true },
    ],
  },
  {
    heading: "Verbal Response",
    subheading: "",
    group: "Glasgow Coma Score",
    dataKey: obsMetricNames.gcsVerbal,
    inputs: [
      { label: "5 Oriented", value: "5", range: { min: 5, max: 5 }, drawBorder: true },
      { label: "4 Confused", value: "4", range: { min: 4, max: 4 }, drawBorder: true },
      { label: "3 Inappropriate words", value: "3", range: { min: 3, max: 3 }, drawBorder: true },
      { label: "2 Incomprehensible sounds", value: "2", range: { min: 2, max: 2 }, drawBorder: true },
      { label: "1 None", value: "1", range: { min: 1, max: 1 }, drawBorder: true },
      { label: "Tubed/Trachy", value: "T", range: { min: 0, max: 0 }, drawBorder: true },
    ],
  },
  {
    heading: "Motor Response",
    subheading: "",
    group: "Glasgow Coma Score",
    dataKey: obsMetricNames.gcsMovement,
    inputs: [
      { label: "6 Obey commands", value: "6", range: { min: 6, max: 6 }, drawBorder: true },
      { label: "5 Localise to pressure", value: "5", range: { min: 5, max: 5 }, drawBorder: true },
      { label: "4 Normal flexion", value: "4", range: { min: 4, max: 4 }, drawBorder: true },
      { label: "3 Abnormal flexion", value: "3", range: { min: 3, max: 3 }, drawBorder: true },
      { label: "2 Extension", value: "2", range: { min: 2, max: 2 }, drawBorder: true },
      { label: "1 None", value: "1", range: { min: 1, max: 1 }, drawBorder: true },
    ],
  },
  {
    heading: "GCS Total",
    subheading: "",
    group: "Glasgow Coma Score",
    dataKey: obsMetricNames.gcsTotal,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 15 }, bold: true },
    ],
  },
  // pupils (we'll do left and right horizontally to simplify things for us)
  {
    heading: "Size",
    subheading: "",
    group: "Pupils",
    groupSubheading: "Right",
    dataKey: obsMetricNames.pupilRightSize,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Reaction",
    subheading: "",
    group: "Pupils",
    groupSubheading: "Right",
    dataKey: obsMetricNames.pupilRightReaction,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 100 } },
    ],
  },
  {
    heading: "Size",
    subheading: "",
    group: "Pupils",
    groupSubheading: "Left",
    dataKey: obsMetricNames.pupilLeftSize,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Reaction",
    subheading: "",
    group: "Pupils",
    groupSubheading: "Left",
    dataKey: obsMetricNames.pupilLeftReaction,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  // limb movement (TODO just check I can remove the ranges here, as these are all text vales, showValue)
  {
    heading: "Right Arm",
    subheading: "",
    group: "Limb Movement",
    dataKey: obsMetricNames.limbMovRightArm,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Left Arm",
    subheading: "",
    group: "Limb Movement",
    dataKey: obsMetricNames.limbMovLeftArm,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Right Leg",
    subheading: "",
    group: "Limb Movement",
    dataKey: obsMetricNames.limbMovRightLeg,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Left Leg",
    subheading: "",
    group: "Limb Movement",
    dataKey: obsMetricNames.limbMovLeftLeg,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  // limb neurovascular status (right arm)
  {
    heading: "Colour",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Arm",
    dataKey: obsMetricNames.limbNeuroRightArmColour,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Warmth",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Arm",
    dataKey: obsMetricNames.limbNeuroRightArmWarmth,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Movemement",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Arm",
    dataKey: obsMetricNames.limbNeuroRightArmMovement,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Sensation",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Arm",
    dataKey: obsMetricNames.limbNeuroRightArmSensation,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Capillary Refill",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Arm",
    dataKey: obsMetricNames.limbNeuroRightArmCapillaryRefill,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Pulse",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Arm",
    dataKey: obsMetricNames.limbNeuroRightArmPulse,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  // limb neurovascular status (left arm)
  {
    heading: "Colour",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Arm",
    dataKey: obsMetricNames.limbNeuroLeftArmColour,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Warmth",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Arm",
    dataKey: obsMetricNames.limbNeuroLeftArmWarmth,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Movemement",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Arm",
    dataKey: obsMetricNames.limbNeuroLeftArmMovement,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Sensation",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Arm",
    dataKey: obsMetricNames.limbNeuroLeftArmSensation,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Capillary Refill",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Arm",
    dataKey: obsMetricNames.limbNeuroLeftArmCapillaryRefill,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Pulse",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Arm",
    dataKey: obsMetricNames.limbNeuroLeftArmPulse,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  // limb neurovascular status (right leg)
  {
    heading: "Colour",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Leg",
    dataKey: obsMetricNames.limbNeuroRightLegColour,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Warmth",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Leg",
    dataKey: obsMetricNames.limbNeuroRightLegWarmth,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Movemement",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Leg",
    dataKey: obsMetricNames.limbNeuroRightLegMovement,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Sensation",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Leg",
    dataKey: obsMetricNames.limbNeuroRightLegSensation,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Capillary Refill",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Leg",
    dataKey: obsMetricNames.limbNeuroRightLegCapillaryRefill,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Pulse",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Right Leg",
    dataKey: obsMetricNames.limbNeuroRightLegPulse,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  // limb neurovascular status (left leg)
  {
    heading: "Colour",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Leg",
    dataKey: obsMetricNames.limbNeuroLeftLegColour,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Warmth",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Leg",
    dataKey: obsMetricNames.limbNeuroLeftLegWarmth,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Movemement",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Leg",
    dataKey: obsMetricNames.limbNeuroLeftLegMovement,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Sensation",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Leg",
    dataKey: obsMetricNames.limbNeuroLeftLegSensation,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Capillary Refill",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Leg",
    dataKey: obsMetricNames.limbNeuroLeftLegCapillaryRefill,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
  {
    heading: "Pulse",
    subheading: "",
    group: "Limb Neurovascular",
    groupSubheading: "Left Leg",
    dataKey: obsMetricNames.limbNeuroLeftLegPulse,
    inputs: [
      { showOriginValue: true, range: { min: 0, max: 10 } },
    ],
  },
];

export type TableSections =
  typeof vitalsSignsTableSections |
  typeof bedsideTableSections |
  typeof ventilatorTableSections |
  typeof urinalysisTableSections |
  typeof neurologicalTableSections;

/**
 * Determine how the cell should be filled. If value does not match input,
 * then render a blank cell. If value does match range and is:
 *  - A number or string (that matches) then fill in a single dot
 *  - An object then assume we are filling over a range
 *    of cells. Start/End cells have a dot and in between cells have a vertical
 *    dotted line.
 *
 * @param value Value from the observation. This can be a number, string or
 *   number range.
 * @param input Stores information about whether the value fits this cell
 */
// eslint-disable-next-line complexity
export const getFillState = (value: any, input: ObsInput) => {
  const isOriginValue = "showOriginValue" in input && input.showOriginValue;
  if (isOriginValue) {
    return FillState.ORIGIN_VALUE;
  }

  if (value === null || value === undefined) {
    return FillState.NONE;
  }


  if (typeof value === "number" && (input as ObsInputRange).range) {
    const limit = (input as ObsInputRange).range;

    if (limit.min <= value && value <= limit.max) {
      return FillState.START_END;
    }
  }

  if (typeof value === "string" && value === (input as ObsInputString).value) {
    return FillState.START_END;
  }

  if (typeof value === "object") {
    const limit = (input as ObsInputRange).range;
    const min = typeof value.diastolic !== "undefined" ? value.diastolic : value.min;
    const max = typeof value.systolic !== "undefined" ? value.systolic : value.min;
    // console.log("check", limit, min, max);
    if (min < limit.min && limit.max < max) {
      // console.log("CONT", FillState.CONTINUE);
      return FillState.CONTINUE;
    }

    if (limit.min !== undefined && limit.max !== undefined) {
      if (limit.min <= min && max <= limit.max) {
        return FillState.START_END;
      }

      if (
        min < limit.min &&
        limit.min <= max &&
        max <= limit.max
      ) {
        return FillState.START;
      }

      if (
        limit.min <= min &&
        min < limit.max &&
        limit.max < max
      ) {
        return FillState.END;
      }
    }
  }

  return FillState.NONE;
};

export const getVitalsSignsTableSections = (observations: Observation[]): ObservationTableSection[] => {
  // If no observations, use default sections
  if (!observations?.length) {
    return vitalsSignsTableSections;
  }

  const {
    hypercapnicRespiratoryFailure: hasHypercapnicRespiratoryFailure,
    o2FlowAboveZero: lastRecordedO2FlowAboveZero,
  } = getLastRecordedObservationValues(observations);

  // if no hasHypercapnicRespiratoryFailure, then calculate o2 flow as normal
  if (!hasHypercapnicRespiratoryFailure) {
    return vitalsSignsTableSections;
  }

  return vitalsSignsTableSections.map(section => {
    if (section.dataKey === obsMetricNames.o2Sats && hasHypercapnicRespiratoryFailure) {
      return {
        ...section,
        inputs: lastRecordedO2FlowAboveZero
          ? O2_SATURATION_LIMITS.hypercapnicWithFlow.ranges
          : O2_SATURATION_LIMITS.hypercapnicZeroFlow.ranges,
      };
    }

    // Modify O2 flow inputs for hypercapnic patients receiving oxygen
    if (section.dataKey === obsMetricNames.o2Flow && hasHypercapnicRespiratoryFailure && lastRecordedO2FlowAboveZero) {
      return {
        ...section,
        inputs: section.inputs.map((input, index) => {
          // First 3 inputs get news: 3 for hypercapnic patients on oxygen
          if (index < 3) {
            return { ...input, news: 3 as const };
          }
          return input;
        }),
      };
    }

    return section;
  });
};
